import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ROUTES } from '../../common-files/enums/server-routes';
import {
  BookingSearchFilterInput,
  normalizeBookingSearchFilters,
} from '../../common-files/booking-search';

@Injectable({ providedIn: 'root' })
export class EstablishmentService {
  constructor(private http: HttpClient) {}

  // branch bookings (the active branch comes from the x-establishment-id header).
  // only non-empty filters are sent.
  listBookings(filters: BookingSearchFilterInput = {}): Observable<any> {
    const normalizedFilters = normalizeBookingSearchFilters(filters);
    let params = new HttpParams();
    Object.entries(normalizedFilters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params = params.set(key, value);
      }
    });
    return this.http.get(ROUTES.ESTAB_BOOKINGS, { params });
  }

  // confirm a single assigned booking -> CONFIRMED (Admin only, gated by can_manage).
  // -- manual mandir utsav booking --

  // utsavs that staff can still book (accommodation not yet over)
  listManualBookingUtsavs(): Observable<any> {
    return this.http.get(`${ROUTES.ESTAB_MANUAL_BOOKING}/utsavs`);
  }

  // family members + saved visitors by familyCode, aadhaarNumber, or self=true
  getManualBookingFamily(search: { familyCode?: string; aadhaarNumber?: string; self?: boolean }): Observable<any> {
    let params = new HttpParams();
    if (search.familyCode) params = params.set('familyCode', search.familyCode);
    if (search.aadhaarNumber) params = params.set('aadhaarNumber', search.aadhaarNumber);
    if (search.self) params = params.set('self', 'true');
    return this.http.get(`${ROUTES.ESTAB_MANUAL_BOOKING}/family`, { params });
  }

  checkManualBookingAvailability(utsavId: number, guestCount: number): Observable<any> {
    return this.http.post(`${ROUTES.ESTAB_MANUAL_BOOKING}/availability`, { utsavId, guestCount });
  }

  createManualBooking(payload: any): Observable<any> {
    return this.http.post(ROUTES.ESTAB_MANUAL_BOOKING, payload);
  }

  confirmBooking(bookingId: number): Observable<any> {
    return this.http.patch(`${ROUTES.ESTAB_BOOKINGS}/${bookingId}/confirm`, {});
  }

  // confirm ALL assigned bookings in the active branch in one go (Admin bulk action).
  confirmAllBookings(): Observable<any> {
    return this.http.post(`${ROUTES.ESTAB_BOOKINGS}/confirm-all`, {});
  }

  // confirm only the ticked assigned bookings (Admin bulk action).
  confirmSelectedBookings(bookingIds: number[]): Observable<any> {
    return this.http.post(`${ROUTES.ESTAB_BOOKINGS}/confirm-selected`, { bookingIds });
  }

  // revoke ALL assigned bookings' rooms in the branch (Admin bulk action):
  // beds are freed and the bookings return to Pending.
  revokeAllAssignments(): Observable<any> {
    return this.http.post(`${ROUTES.ESTAB_BOOKINGS}/revoke-all`, {});
  }

  // revoke one booking's whole room assignment (cancelled/rejected rows).
  revokeAssignment(bookingId: number): Observable<any> {
    return this.http.post(`${ROUTES.ESTAB_BOOKINGS}/${bookingId}/revoke-assignment`, {});
  }

  // just the room/bed assignment of a booking (lighter than getBooking).
  getAllocation(bookingId: number): Observable<any> {
    return this.http.get(`${ROUTES.ESTAB_BOOKINGS}/${bookingId}/allocation`);
  }

  // properties assignable to a booking (utsav-scoped for utsav bookings).
  getAvailableProperties(bookingId: number): Observable<any> {
    return this.http.get(`${ROUTES.ESTAB_BOOKINGS}/${bookingId}/available-properties`);
  }

  // rooms of a property with per-bed availability for the assign dialog.
  getAvailableRooms(bookingId: number, propertyId: number): Observable<any> {
    const params = new HttpParams().set('propertyId', propertyId);
    return this.http.get(`${ROUTES.ESTAB_BOOKINGS}/${bookingId}/available-rooms`, { params });
  }

  cancelBooking(bookingId: number, reason: string): Observable<any> {
    return this.http.patch(`${ROUTES.ESTAB_BOOKINGS}/${bookingId}/cancel`, { reason });
  }

  rejectBooking(bookingId: number, reason: string): Observable<any> {
    return this.http.patch(`${ROUTES.ESTAB_BOOKINGS}/${bookingId}/reject`, { reason });
  }

  // both frontdesk lists (ready to check in + currently staying), enriched with
  // the booker's name, guests (unmasked aadhaar) and rooms/properties.
  getCheckinCheckoutLists(): Observable<any> {
    return this.http.get(ROUTES.ESTAB_CHECKIN_CHECKOUT_LISTS);
  }

  // check a booking in (requires it to be ALLOCATED) — no body, id in the path.
  checkIn(bookingId: number): Observable<any> {
    return this.http.post(`${ROUTES.ESTAB_BOOKINGS}/${bookingId}/checkin`, {});
  }

  // check a booking out (requires it to be CHECKED_IN).
  checkOut(bookingId: number): Observable<any> {
    return this.http.post(`${ROUTES.ESTAB_BOOKINGS}/${bookingId}/checkout`, {});
  }

  // branch master lists (read-only for the Operator; branch from the header).
  // activeOnly = true is used by the top-menu property selector (active only).
  listProperties(activeOnly: boolean = false): Observable<any> {
    let params = new HttpParams();
    if (activeOnly) params = params.set('activeOnly', 'true');
    return this.http.get(ROUTES.ESTAB_PROPERTIES, { params });
  }

  listRooms(propertyId: number): Observable<any> {
    const params = new HttpParams().set('propertyId', propertyId);
    return this.http.get(ROUTES.ESTAB_ROOMS, { params });
  }

  listUtsavs(): Observable<any> {
    return this.http.get(ROUTES.ESTAB_UTSAVS);
  }

  // per-utsav occupancy report (target vs assigned capacity + occupied beds).
  getOccupancyReport(utsavId: number): Observable<any> {
    const params = new HttpParams().set('utsavId', utsavId);
    return this.http.get(ROUTES.ESTAB_REPORTS_OCCUPANCY, { params });
  }

  // booking funnel + guest totals for an utsav.
  getBookingSummaryReport(utsavId: number): Observable<any> {
    const params = new HttpParams().set('utsavId', utsavId);
    return this.http.get(ROUTES.ESTAB_REPORTS_SUMMARY, { params });
  }

  // per-property room / bed utilization for an utsav.
  getUtilizationReport(utsavId: number): Observable<any> {
    const params = new HttpParams().set('utsavId', utsavId);
    return this.http.get(ROUTES.ESTAB_REPORTS_UTILIZATION, { params });
  }

  // overdue check-ins (no-shows) and overdue check-outs for an utsav.
  getNoShowsReport(utsavId: number): Observable<any> {
    const params = new HttpParams().set('utsavId', utsavId);
    return this.http.get(ROUTES.ESTAB_REPORTS_NO_SHOWS, { params });
  }

  // beds occupied per day across the utsav window vs assigned capacity.
  getOccupancyTimelineReport(utsavId: number): Observable<any> {
    const params = new HttpParams().set('utsavId', utsavId);
    return this.http.get(ROUTES.ESTAB_REPORTS_TIMELINE, { params });
  }

  // expected arrivals / departures per day with actuals so far.
  getMovementsReport(utsavId: number): Observable<any> {
    const params = new HttpParams().set('utsavId', utsavId);
    return this.http.get(ROUTES.ESTAB_REPORTS_MOVEMENTS, { params });
  }

  // demand by booking size vs assigned capacity by room type.
  getDemandSupplyReport(utsavId: number): Observable<any> {
    const params = new HttpParams().set('utsavId', utsavId);
    return this.http.get(ROUTES.ESTAB_REPORTS_DEMAND_SUPPLY, { params });
  }

  // cancellations & rejections with reasons and beds still held.
  getCancellationsReport(utsavId: number): Observable<any> {
    const params = new HttpParams().set('utsavId', utsavId);
    return this.http.get(ROUTES.ESTAB_REPORTS_CANCELLATIONS, { params });
  }

  // bookings created per day with running totals.
  getBookingVelocityReport(utsavId: number): Observable<any> {
    const params = new HttpParams().set('utsavId', utsavId);
    return this.http.get(ROUTES.ESTAB_REPORTS_VELOCITY, { params });
  }

  // guests by type / gender, bookings by size and type.
  getGuestCompositionReport(utsavId: number): Observable<any> {
    const params = new HttpParams().set('utsavId', utsavId);
    return this.http.get(ROUTES.ESTAB_REPORTS_GUESTS, { params });
  }

  // who checked guests in / out, when, and arrival delays.
  getFrontdeskActivityReport(utsavId: number): Observable<any> {
    const params = new HttpParams().set('utsavId', utsavId);
    return this.http.get(ROUTES.ESTAB_REPORTS_FRONTDESK, { params });
  }

  // branch settings (mandir details) — view + edit (Admin+, gated by can_manage).
  getMandir(): Observable<any> {
    return this.http.get(ROUTES.ESTAB_MANDIR);
  }

  updateMandir(payload: any): Observable<any> {
    return this.http.put(ROUTES.ESTAB_MANDIR, payload);
  }

  // property master management (Admin+, gated by can_manage on 'properties').
  addProperty(payload: any): Observable<any> {
    return this.http.post(ROUTES.ESTAB_PROPERTIES, payload);
  }

  updateProperty(propertyId: number, payload: any): Observable<any> {
    return this.http.put(`${ROUTES.ESTAB_PROPERTIES}/${propertyId}`, payload);
  }

  setPropertyStatus(propertyId: number, status: number): Observable<any> {
    return this.http.patch(`${ROUTES.ESTAB_PROPERTIES}/${propertyId}/status`, { status });
  }

  // room master management (Admin+, gated by can_manage on 'rooms').
  addRoom(payload: any): Observable<any> {
    return this.http.post(ROUTES.ESTAB_ROOMS, payload);
  }

  updateRoom(roomId: number, payload: any): Observable<any> {
    return this.http.put(`${ROUTES.ESTAB_ROOMS}/${roomId}`, payload);
  }

  setRoomStatus(roomId: number, status: number): Observable<any> {
    return this.http.patch(`${ROUTES.ESTAB_ROOMS}/${roomId}/status`, { status });
  }

  // one booking with its guests + current room/bed allocation.
  getBooking(bookingId: number): Observable<any> {
    return this.http.get(`${ROUTES.ESTAB_BOOKINGS}/${bookingId}`);
  }

  // allocate rooms/beds to a booking (Operator+, gated by can_allocate).
  allocateRooms(
    bookingId: number,
    allocations: { roomId: number; bedNumbers: string[] }[]
  ): Observable<any> {
    return this.http.post(`${ROUTES.ESTAB_BOOKINGS}/${bookingId}/allocate`, { allocations });
  }

  // release an allocated room from a booking.
  releaseRoom(bookingId: number, roomId: number): Observable<any> {
    return this.http.delete(`${ROUTES.ESTAB_BOOKINGS}/${bookingId}/rooms/${roomId}`);
  }

  // branch staff management (Admin+, gated by can_manage_users on 'branch-staff').
  // establishmentId is passed explicitly (the establishment endpoints read it from
  // query/body, not the header) so it works for single-branch and all-branch admins.
  listStaff(establishmentId: number | null): Observable<any> {
    let params = new HttpParams();
    if (establishmentId) params = params.set('establishmentId', establishmentId);
    return this.http.get(ROUTES.ESTAB_STAFF, { params });
  }

  addStaff(payload: any): Observable<any> {
    return this.http.post(ROUTES.ESTAB_STAFF, payload);
  }

  setStaffRestricted(sumId: number, restricted: boolean): Observable<any> {
    const action = restricted ? 'restrict' : 'unrestrict';
    return this.http.post(`${ROUTES.ESTAB_STAFF}/${sumId}/${action}`, {});
  }

  removeStaff(sumId: number, establishmentId: number | null): Observable<any> {
    let params = new HttpParams();
    if (establishmentId) params = params.set('establishmentId', establishmentId);
    return this.http.delete(`${ROUTES.ESTAB_STAFF}/${sumId}`, { params });
  }

  // staff access: roles + permissions grouped Establishment -> Role -> View -> Permission.
  getStaffAccess(sumId: number): Observable<any> {
    return this.http.get(`${ROUTES.ESTAB_STAFF}/${sumId}/access`);
  }

  // roles / view-permissions the signed-in admin may hand out (below their own weight).
  listAssignableRoles(): Observable<any> {
    return this.http.get(`${ROUTES.ESTAB_STAFF}/assignable-roles`);
  }

  listAssignablePermissions(): Observable<any> {
    return this.http.get(`${ROUTES.ESTAB_STAFF}/assignable-permissions`);
  }

  assignStaffRole(sumId: number, payload: { establishmentId: number; roleKey: string; validUntil?: string | null }): Observable<any> {
    return this.http.post(`${ROUTES.ESTAB_STAFF}/${sumId}/roles`, payload);
  }

  setStaffRoleStatus(sumId: number, suremId: number, active: boolean): Observable<any> {
    return this.http.patch(`${ROUTES.ESTAB_STAFF}/${sumId}/roles/${suremId}/status`, { active });
  }

  grantStaffPermission(sumId: number, suremId: number, svpId: number): Observable<any> {
    return this.http.post(`${ROUTES.ESTAB_STAFF}/${sumId}/permissions`, { suremId, svpId });
  }

  // several permissions at once (a whole view)
  setStaffPermissionsStatus(sumId: number, supdIds: number[], active: boolean): Observable<any> {
    return this.http.patch(`${ROUTES.ESTAB_STAFF}/${sumId}/permissions/status`, { supdIds, active });
  }

  setStaffPermissionStatus(sumId: number, supdId: number, active: boolean): Observable<any> {
    return this.http.patch(`${ROUTES.ESTAB_STAFF}/${sumId}/permissions/${supdId}/status`, { active });
  }

  // utsav master management (Admin+, gated by can_manage on 'utsav-setup').
  addUtsav(payload: any): Observable<any> {
    return this.http.post(ROUTES.ESTAB_UTSAVS, payload);
  }

  updateUtsav(utsavId: number, payload: any): Observable<any> {
    return this.http.put(`${ROUTES.ESTAB_UTSAVS}/${utsavId}`, payload);
  }

  uploadUtsavImage(file: File): Observable<any> {
    const form = new FormData();
    form.append('file', file);
    return this.http.post(ROUTES.ESTAB_UTSAV_IMAGE, form);
  }

  setUtsavStatus(utsavId: number, status: number): Observable<any> {
    return this.http.patch(`${ROUTES.ESTAB_UTSAVS}/${utsavId}/status`, { status });
  }

  // utsav property/room assignment + inventory target (Admin+).
  getUtsavAssignments(utsavId: number): Observable<any> {
    return this.http.get(`${ROUTES.ESTAB_UTSAVS}/${utsavId}/assignments`);
  }

  getAssignableProperties(utsavId: number): Observable<any> {
    return this.http.get(`${ROUTES.ESTAB_UTSAVS}/${utsavId}/assignable-properties`);
  }

  setInventoryTarget(utsavId: number, payload: any): Observable<any> {
    return this.http.put(`${ROUTES.ESTAB_UTSAVS}/${utsavId}/inventory`, payload);
  }

  assignProperty(utsavId: number, propertyId: number): Observable<any> {
    return this.http.post(`${ROUTES.ESTAB_UTSAVS}/${utsavId}/assign-property`, { propertyId });
  }

  unassignProperty(utsavId: number, propertyId: number): Observable<any> {
    return this.http.delete(`${ROUTES.ESTAB_UTSAVS}/${utsavId}/properties/${propertyId}`);
  }

  unassignRoom(utsavId: number, roomId: number): Observable<any> {
    return this.http.delete(`${ROUTES.ESTAB_UTSAVS}/${utsavId}/rooms/${roomId}`);
  }

  // per-date occupied-bed counts of a room for one utsav (assigned-status calendar).
  getRoomOccupancy(roomId: number, utsavId: number): Observable<any> {
    const params = new HttpParams().set('utsavId', utsavId);
    return this.http.get(`${ROUTES.ESTAB_ROOMS}/${roomId}/occupancy`, { params });
  }

  // activate an utsav (can_activate — Central Establishment Admin only).
  activateUtsav(utsavId: number): Observable<any> {
    return this.http.patch(`${ROUTES.ESTAB_UTSAVS}/${utsavId}/activate`, {});
  }

  // assign existing branch rooms to the utsav; the rooms' properties are
  // assigned along with them (server derives the property from each room).
  addRoomsToUtsav(utsavId: number, roomIds: number[]): Observable<any> {
    return this.http.post(`${ROUTES.ESTAB_UTSAVS}/${utsavId}/rooms`, { roomIds });
  }

  // create a new property + its rooms on the branch AND assign them to the utsav.
  addNewPropertyToUtsav(utsavId: number, payload: any): Observable<any> {
    return this.http.post(`${ROUTES.ESTAB_UTSAVS}/${utsavId}/new-property`, payload);
  }
}
